import { useEffect, useState, useRef, useCallback } from 'react';
import { ChevronDown, Globe, Check, AlertCircle, RefreshCw } from 'lucide-react';
import useGoogleTranslate from '../../hooks/useGoogleTranslate';
import useAnalytics from '../../hooks/useAnalytics';

const languages = [
  { code: 'af', name: 'Afrikaans' },
  { code: 'sq', name: 'Albanian' },
  { code: 'am', name: 'Amharic' },
  { code: 'ar', name: 'Arabic' },
  { code: 'hy', name: 'Armenian' },
  { code: 'az', name: 'Azerbaijani' },
  { code: 'eu', name: 'Basque' },
  { code: 'bn', name: 'Bengali' },
  { code: 'my', name: 'Burmese' },
  { code: 'bg', name: 'Bulgarian' },
  { code: 'ca', name: 'Catalan' },
  { code: 'zh-CN', name: 'Chinese (Simplified)' },
  { code: 'zh-TW', name: 'Chinese (Traditional)' },
  { code: 'hr', name: 'Croatian' },
  { code: 'cs', name: 'Czech' },
  { code: 'da', name: 'Danish' },
  { code: 'nl', name: 'Dutch' },
  { code: 'en', name: 'English' },
  { code: 'et', name: 'Estonian' },
  { code: 'fi', name: 'Finnish' },
  { code: 'fr', name: 'French' },
  { code: 'gl', name: 'Galician' },
  { code: 'ka', name: 'Georgian' },
  { code: 'de', name: 'German' },
  { code: 'el', name: 'Greek' },
  { code: 'gu', name: 'Gujarati' },
  { code: 'ht', name: 'Haitian Creole' },
  { code: 'ha', name: 'Hausa' },
  { code: 'haw', name: 'Hawaiian' },
  { code: 'he', name: 'Hebrew' },
  { code: 'hi', name: 'Hindi' },
  { code: 'hmn', name: 'Hmong' },
  { code: 'hu', name: 'Hungarian' },
  { code: 'is', name: 'Icelandic' },
  { code: 'ig', name: 'Igbo' },
  { code: 'id', name: 'Indonesian' },
  { code: 'ga', name: 'Irish' },
  { code: 'it', name: 'Italian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'jw', name: 'Javanese' },
  { code: 'kn', name: 'Kannada' },
  { code: 'kk', name: 'Kazakh' },
  { code: 'km', name: 'Khmer' },
  { code: 'ko', name: 'Korean' },
  { code: 'ku', name: 'Kurdish (Kurmanji)' },
  { code: 'ky', name: 'Kyrgyz' },
  { code: 'lo', name: 'Lao' },
  { code: 'lv', name: 'Latvian' },
  { code: 'lt', name: 'Lithuanian' },
  { code: 'lb', name: 'Luxembourgish' },
  { code: 'mk', name: 'Macedonian' },
  { code: 'mg', name: 'Malagasy' },
  { code: 'ms', name: 'Malay' },
  { code: 'ml', name: 'Malayalam' },
  { code: 'mt', name: 'Maltese' },
  { code: 'mi', name: 'Maori' },
  { code: 'mr', name: 'Marathi' },
  { code: 'mn', name: 'Mongolian' },
  { code: 'ne', name: 'Nepali' },
  { code: 'no', name: 'Norwegian' },
  { code: 'ps', name: 'Pashto' },
  { code: 'fa', name: 'Persian' },
  { code: 'pl', name: 'Polish' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'pa', name: 'Punjabi' },
  { code: 'ro', name: 'Romanian' },
  { code: 'ru', name: 'Russian' },
  { code: 'sm', name: 'Samoan' },
  { code: 'gd', name: 'Scots Gaelic' },
  { code: 'sr', name: 'Serbian' },
  { code: 'st', name: 'Sesotho' },
  { code: 'sn', name: 'Shona' },
  { code: 'sd', name: 'Sindhi' },
  { code: 'si', name: 'Sinhala' },
  { code: 'sk', name: 'Slovak' },
  { code: 'sl', name: 'Slovenian' },
  { code: 'so', name: 'Somali' },
  { code: 'es', name: 'Spanish' },
  { code: 'su', name: 'Sundanese' },
  { code: 'sw', name: 'Swahili' },
  { code: 'sv', name: 'Swedish' },
  { code: 'tg', name: 'Tajik' },
  { code: 'ta', name: 'Tamil' },
  { code: 'tt', name: 'Tatar' },
  { code: 'te', name: 'Telugu' },
  { code: 'th', name: 'Thai' },
  { code: 'tr', name: 'Turkish' },
  { code: 'tk', name: 'Turkmen' },
  { code: 'uk', name: 'Ukrainian' },
  { code: 'ur', name: 'Urdu' },
  { code: 'ug', name: 'Uyghur' },
  { code: 'uz', name: 'Uzbek' },
  { code: 'vi', name: 'Vietnamese' },
  { code: 'cy', name: 'Welsh' },
  { code: 'xh', name: 'Xhosa' },
  { code: 'yi', name: 'Yiddish' },
  { code: 'yo', name: 'Yoruba' },
  { code: 'zu', name: 'Zulu' }
];

const DEFAULT_LANGUAGE = 'en';
const MAX_ERROR_COUNT = 5;
const LANGUAGE_CHANGE_TIMEOUT = 10000; // 10 seconds timeout for language change

const LanguageSelector = ({ isMobile = false }) => {
  const {
    changeLanguage,
    isInitialized,
    isLoading: hookIsLoading,
    error: apiError,
    resetService
  } = useGoogleTranslate();
  const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX';
  const { trackEvent } = useAnalytics(GA_MEASUREMENT_ID);

  // Local loading state to better control UI feedback
  const [isChanging, setIsChanging] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    return localStorage.getItem('selectedLanguage') || DEFAULT_LANGUAGE;
  });

  const [uiError, setUiError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [serviceDisabled, setServiceDisabled] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const [lastAttemptedLanguage, setLastAttemptedLanguage] = useState(null);

  const dropdownRef = useRef(null);
  const isChangingLanguage = useRef(false);
  const languageChangeTimer = useRef(null);
  const errorDisplayTimer = useRef(null);
  const languageChangeTimeout = useRef(null);

  // Combine hook loading state with local changing state
  const isLoading = hookIsLoading || isChanging;

  // Set initial language from localStorage
  useEffect(() => {
    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (savedLanguage && languages.some(lang => lang.code === savedLanguage)) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  // Track successful language changes
  useEffect(() => {
    // If we have a lastAttemptedLanguage and the currentLanguage matches it,
    // this means the language change was successful
    if (lastAttemptedLanguage && lastAttemptedLanguage === currentLanguage) {
      // Only track successful changes
      trackEvent({
        category: 'Language Change',
        action: 'Change Success',
        label: `Language=${currentLanguage}`,
      });
      
      // Reset the last attempted language
      setLastAttemptedLanguage(null);
    }
  }, [currentLanguage, lastAttemptedLanguage, trackEvent]);

  // Handle errors from the hook
  useEffect(() => {
    if (apiError) {
      setUiError(apiError);

      // Track error event
      if (lastAttemptedLanguage) {
        trackEvent({
          category: 'Language Change',
          action: 'Error',
          label: `Failed=${lastAttemptedLanguage}, Error=${apiError}`,
        });
        
        setLastAttemptedLanguage(null);
      }

      // Increment error count
      setErrorCount(prev => {
        const newCount = prev + 1;

        // Disable service after multiple failures
        if (newCount >= MAX_ERROR_COUNT) {
          setServiceDisabled(true);
        }

        return newCount;
      });

      // Auto-clear error message after 5 seconds
      clearTimeout(errorDisplayTimer.current);
      errorDisplayTimer.current = setTimeout(() => {
        setUiError(null);
      }, 5000);
    }
  }, [apiError, lastAttemptedLanguage, trackEvent]);

  // Handle outside clicks to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      clearTimeout(languageChangeTimer.current);
      clearTimeout(errorDisplayTimer.current);
      clearTimeout(languageChangeTimeout.current);
    };
  }, []);

  // Reset the translation service
  const handleReset = useCallback(() => {
    setUiError(null);
    setServiceDisabled(false);
    setErrorCount(0);
    setIsChanging(false);
    isChangingLanguage.current = false;
    clearTimeout(languageChangeTimeout.current);
    
    trackEvent({
      category: 'Language Change',
      action: 'Service Reset',
    });
    
    resetService();
  }, [resetService, trackEvent]);

  // Safely change language with better error handling
  const safeLanguageChange = useCallback(async (langCode) => {
    if (serviceDisabled || isChangingLanguage.current) {
      return;
    }

    // Track attempt immediately
    trackEvent({
      category: 'Language Change',
      action: 'Attempt',
      label: `Attempting=${langCode}`,
    });
    
    // Store the language we're attempting to change to
    setLastAttemptedLanguage(langCode);

    isChangingLanguage.current = true;
    setIsChanging(true);
    setUiError(null);

    try {
      await changeLanguage(langCode);
      setCurrentLanguage(langCode);
      localStorage.setItem('selectedLanguage', langCode);
      setIsOpen(false);
      setErrorCount(0);

      // Add a small delay and then force a translation refresh
      setTimeout(() => {
        if (window.google?.translate?.TranslateElement) {
          window.google.translate.TranslateElement().refresh();
        }
      }, 1000);
    } catch (error) {
      console.error('Language change failed:', error);
      setUiError('Failed to change language. Please try again.');

      trackEvent({
        category: 'Language Change',
        action: 'Error',
        label: `Failed=${langCode}, Error=${error.message || 'Unknown error'}`,
      });

      // Fall back to English if language change fails
      if (langCode !== DEFAULT_LANGUAGE) {
        setCurrentLanguage(DEFAULT_LANGUAGE);
        localStorage.setItem('selectedLanguage', DEFAULT_LANGUAGE);
      }

      setErrorCount(prev => {
        const newCount = prev + 1;
        if (newCount >= MAX_ERROR_COUNT) {
          setServiceDisabled(true);
        }
        return newCount;
      });
    } finally {
      isChangingLanguage.current = false;
      setIsChanging(false);
    }
  }, [serviceDisabled, changeLanguage, trackEvent]);

  const toggleDropdown = useCallback(() => {
    if (isLoading) return;
    
    // Track dropdown toggle
    if (!isOpen) {
      trackEvent({
        category: 'Language Change',
        action: 'Open Dropdown',
      });
    }
    
    setIsOpen(prev => !prev);
  }, [isLoading, isOpen, trackEvent]);

  const currentLanguageObj = languages.find(lang => lang.code === currentLanguage) ||
    languages.find(lang => lang.code === DEFAULT_LANGUAGE);

  // If service is completely disabled, show with reset option
  if (serviceDisabled) {
    return (
      <div className={`${isMobile ? 'w-full' : ''} relative`}>
        <div className="flex items-center gap-2">
          <button
            className={`flex items-center gap-2 ${isMobile
                ? 'w-full justify-between px-4 py-3 bg-gray-50 rounded-lg'
                : 'px-3 py-2 rounded-full bg-gray-50'
              } transition-all duration-200`}
            title="Translation service unavailable"
          >
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-gray-400" />
              <div className="flex items-center gap-1">
                <span className="font-medium text-gray-500 notranslate">
                  {currentLanguageObj?.code.toUpperCase() || 'EN'}
                </span>
                {!isMobile && (
                  <span className="hidden md:inline text-sm text-gray-400 notranslate">
                    - {currentLanguageObj?.name || 'English'}
                  </span>
                )}
              </div>
            </div>
            <AlertCircle className="w-4 h-4 text-gray-400" />
          </button>

          <button
            onClick={handleReset}
            className="p-2 text-gray-500 hover:bg-gray-100 rounded-full"
            title="Reset translation service"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${isMobile ? 'w-full' : ''} relative`} ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        disabled={isLoading}
        className={`flex items-center gap-2 ${isMobile
            ? 'w-full justify-between px-4 py-3 bg-gray-50 rounded-lg'
            : 'px-3 py-2 rounded-full hover:bg-gray-50'
          } transition-all duration-200 ${isLoading ? 'opacity-70' : ''}`}
        aria-expanded={isOpen}
        aria-busy={isLoading}
      >
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-gray-600" />
          <div className="flex items-center gap-1">
            <span className="font-medium text-gray-700 notranslate">
              {currentLanguageObj?.code.toUpperCase() || 'EN'}
            </span>
            {!isMobile && (
              <span className="hidden md:inline text-sm text-gray-600 notranslate">
                - {currentLanguageObj?.name || 'English'}
              </span>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="animate-spin">
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </div>
        ) : (
          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </button>

      {/* Error message */}
      {uiError && (
        <div className="absolute left-0 top-full mt-1 p-2 bg-red-50 border border-red-100 rounded-md text-xs text-red-600 w-48 z-50">
          <div className="flex items-start gap-1">
            <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <div>{uiError}</div>
          </div>
        </div>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className={`absolute ${isMobile ? 'left-0 right-0' : 'left-0'} top-full mt-2 bg-white shadow-lg rounded-lg border border-gray-100 py-2 overflow-hidden z-50 w-64 max-h-72 overflow-y-auto`}>
          <div className="sticky top-0 bg-white py-1 px-3 border-b border-gray-100">
            <p className="text-xs text-gray-400">Select a language</p>
          </div>

          <div className="pt-1">
            {languages.map((language) => (
              <button
                key={language.code}
                onClick={() => {
                  safeLanguageChange(language.code);
                }}
                disabled={isLoading}
                className={`w-full text-left px-3 py-2 flex items-center justify-between hover:bg-gray-50 ${currentLanguage === language.code ? 'bg-gray-50' : ''
                  } ${isLoading ? 'opacity-50 cursor-default' : 'cursor-pointer'}`}
              >
                <span className={`text-sm ${currentLanguage === language.code ? 'font-medium' : ''} notranslate`}>
                  {language.name}
                </span>
                {currentLanguage === language.code && (
                  <Check className="w-4 h-4 text-blue-500" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Hidden element for Google Translate to attach to */}
      <div id="google_translate_element" style={{ display: 'none' }}></div>
    </div>
  );
};

export default LanguageSelector;